import { useEffect, useState, forwardRef, useRef, useCallback } from 'react';
import { faCircleXmark, faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import DOMPurify from 'dompurify';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command.component';
import { searchCitiesByTerm } from '@/services/cities.service';
import { City } from '@/types/city.type';
import { getLogger } from '@/logger/logger';

type SelectCitiesProps = {
  selectedCity: City | null;
  onChange: (city: City | null) => void;
  hasError?: boolean;
  placeholder?: string;
};

const SelectCities = forwardRef<HTMLInputElement, SelectCitiesProps>(
  ({ selectedCity, onChange, hasError, placeholder = 'Digite o nome da cidade' }, ref) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const setRefs = useCallback((element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }, [ref]);

    useEffect(() => {
      if (selectedCity) {
        setInputValue(`${selectedCity.city} - ${selectedCity.uf}`);
      } else if (!open) {
        setInputValue('');
      }
    }, [selectedCity, open]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };

      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open]);

    const handleInputChange = (value: string) => {
      const sanitizedValue = DOMPurify.sanitize(value);
      setInputValue(sanitizedValue);
      if (sanitizedValue.length > 0) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    };

    const handleClearSelection = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null);
      setInputValue('');
      setOpen(false);
      inputRef.current?.focus();
    };

    return (
      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          <input
            key="select-cities-input"
            ref={setRefs}
            type="text"
            data-testid={DataTestIdEnum.SELECT_CITIES_COMMAND_INPUT}
            className={
              `w-full border rounded-lg transition-all outline-none min-h-[3rem]
              bg-white py-2 px-4 text-gray-700 placeholder:text-gray-500 mt-1
              border-gray-300 focus-within:border-gray-500
              ${hasError && !open ? 'border-danger-600' : ''}
              ${selectedCity ? 'pr-10' : ''}`
            }
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => {
              handleInputChange(e.target.value);
            }}
          />
          {selectedCity
            ? (
                <button
                  data-testid={DataTestIdEnum.SELECT_CITIES_CLEAR_BUTTON}
                  type="button"
                  onClick={handleClearSelection}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors mt-[2px] pr-1"
                  aria-label="Limpar seleção"
                >
                  <Icon icon={faCircleXmark} className="w-4 h-4" />
                </button>
              )
            : (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none pr-1">
                  <Icon icon={open ? faChevronUp : faChevronDown} className="w-4 h-4" />
                </div>
              )}
        </div>
        {open && (
          <div className="absolute z-50 w-full p-0 bg-white rounded-lg border border-gray-500 shadow-md">
            <CityList
              inputValue={inputValue}
              setOpen={setOpen}
              setSelectedCity={onChange}
              onInputChange={handleInputChange}
            />
          </div>
        )}
      </div>
    );
  },
);

SelectCities.displayName = 'SelectCities';

function CityList({
  inputValue,
  setOpen,
  setSelectedCity,
}: {
  inputValue: string;
  setOpen: (open: boolean) => void;
  setSelectedCity: (city: City | null) => void;
  onInputChange: (value: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const debouncedInputValue = useDebounceValue(inputValue, 300);

  useEffect(() => {
    async function fetchCitiesByTerm() {
      if (debouncedInputValue.length >= 3) {
        setLoading(true);
        try {
          const data = await searchCitiesByTerm(debouncedInputValue);
          setCities(data.results);
        } catch (error) {
          const logger = getLogger('SelectCitiesComponent');
          logger.warn('Error fetching cities by term', { debouncedInputValue, error });
        } finally {
          setLoading(false);
        }
      } else {
        setCities([]);
      }
    }

    fetchCitiesByTerm();
  }, [debouncedInputValue]);

  const handleSelect = (value: string) => {
    const selectedCity = cities.find((city) => `${city.city} - ${city.uf}` === value) || null;
    setSelectedCity(selectedCity);
    setOpen(false);
  };

  return (
    <Command shouldFilter={false}>
      <CommandList className="max-h-[300px]">
        {inputValue.length > 0 && inputValue.length < 3 && (
          <CommandEmpty data-testid={DataTestIdEnum.SELECT_CITIES_COMMAND_EMPTY} className="p-4 text-center">
            <span className="text-base text-gray-500">
              Digite ao menos 3 caracteres
            </span>
          </CommandEmpty>
        )}
        {inputValue.length >= 3 && !loading && cities.length === 0 && (
          <CommandEmpty data-testid={DataTestIdEnum.SELECT_CITIES_COMMAND_EMPTY} className="p-4 text-center">
            <span className="text-base text-gray-900">
              Cidade não encontrada. Verifique o nome.
            </span>
          </CommandEmpty>
        )}
        {loading && (
          <CommandEmpty>
            <div className="loading loading-lg"></div>
          </CommandEmpty>
        )}
        {cities.length > 0 && (
          <CommandGroup>
            {cities.map((city, index) => {
              const key = `${city.city} - ${city.uf}`;
              return (
                <CommandItem
                  data-testid={`${DataTestIdEnum.SELECT_CITIES_COMMAND_ITEM}-${index}`}
                  key={key}
                  value={key}
                  onSelect={handleSelect}
                  className="hover:cursor-pointer"
                >
                  {city.city}
                  {' '}
                  -
                  {' '}
                  {city.uf}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}

export default SelectCities;
